import React, { useEffect, useMemo, useRef, useState } from 'react';
import Container from '@/components/ui/Container';
import { PaymentMethodRef } from '@/components/ui/PaymentMethodEntry';
import ForMoreInquiry from '@/components/ui/ForMoreInquiry';
import BottomBar from '@/components/ui/BottomBar';
import { toast } from 'react-toastify';
import { StyledQRSeatBookingPageContent } from '@/views/QRView/QRSeatBookingPage/QRSeatBookingPage.styled.ts';
import RevelQrLoading from '@/components/ui/RevelQrLoading';
import { useParams } from 'react-router-dom';
import useReservationSchedule from '@/hooks/useEvent.ts';
import venueMaps from '@/data/venueMapData';
import AreaSelector from '@/components/ui/AreaSelector';
import BusinessBrandBar from '@/components/ui/BusinessBrandBar';
import { EventDetailStatus, VenueArea } from '@/apiModels/ReservationScheduleAPIModel';
import AdminSeatSelector from '@/components/ui/AdminAreaSelector';
import useListSeat from '@/hooks/useListSeat.ts';

const AdminTableReservationView: React.FunctionComponent = () => {
  const { id: qrCode } = useParams();
  const paymentRef = useRef<PaymentMethodRef>(null);
  const [bookingId] = useState<string>('');
  const [businessId, setBusinessId] = useState<string>('');

  const [emptyReservationsError] = useState('');

  const scrollToSeatSelectorRef = useRef<HTMLDivElement>(null);

  const {
    data: event,
    isLoading: isReservationScheduleLoading,
    error: reservationScheduleError,
  } = useReservationSchedule({ scheduleQrCode: qrCode });

  const [selectedArea, setSelectedArea] = useState<VenueArea>(() => {
    const defaultArea = event?.venue_map.areas.find((area) => area.is_default);

    if (!defaultArea) {
      return {
        id: '',
        name: '',
        is_default: false,
      };
    }

    return defaultArea;
  });

  const { data, isLoading, error } = useListSeat({
    eventId: event?.id,
    seatId: selectedArea.id,
  });

  useEffect(() => {
    if (event) {
      const defaultArea = event.venue_map?.areas.find((area) => area.is_default);

      if (defaultArea) {
        setSelectedArea(defaultArea);
      }
    }
  }, [event]);

  const getVenueMap = useMemo(() => {
    if (!isReservationScheduleLoading && !reservationScheduleError) {
      setBusinessId(event!.organizer);

      const filteredMapArea = venueMaps
        .flatMap((venueMap) => venueMap.areas)
        .find((area) => area.id === selectedArea.id);

      if (filteredMapArea) {
        return filteredMapArea;
      }
    }
  }, [selectedArea, isReservationScheduleLoading, reservationScheduleError, setBusinessId, event]);
  const onBottomClick = () => {
    toast.success('Please select a table');
  };

  useEffect(() => {
    localStorage.removeItem('switch');
  }, []);

  useEffect(() => {
    if (bookingId) {
      localStorage.setItem('businessId', businessId);
      localStorage.setItem('bookingId', bookingId);
      paymentRef?.current!.initPayment();
    }
  }, [businessId, bookingId]);
  if (isLoading || isReservationScheduleLoading || !selectedArea) {
    return <RevelQrLoading />;
  }
  if (error || reservationScheduleError || !event) {
    throw new Error();
  }

  return (
    <React.Fragment>
      <StyledQRSeatBookingPageContent
        $isDisabled={
          event.status == EventDetailStatus.CLOSED || event.status !== EventDetailStatus.OPENED
        }
      >
        <Container>
          <div ref={scrollToSeatSelectorRef}></div>
          <AreaSelector
            selectedArea={selectedArea}
            areas={event.venue_map.areas}
            setSelectedArea={setSelectedArea}
          />

          {getVenueMap && (
            <AdminSeatSelector
              errorText={emptyReservationsError}
              data={data}
              venueMap={getVenueMap}
              eventId={event?.id || ''}
            />
          )}

          <BusinessBrandBar
            eventName={event!.name}
            brandIcon={event.venue.image}
            brandName={event.venue.name}
            brandAddress={event.venue.address}
          />

          <ForMoreInquiry />
        </Container>
      </StyledQRSeatBookingPageContent>

      <BottomBar
        buttonLabel={
          event.status === EventDetailStatus.CLOSED
            ? 'Closed'
            : event.status === EventDetailStatus.SOLD_OUT
              ? 'Sold Out'
              : event.status === EventDetailStatus.COMPLETED
                ? 'Completed'
                : 'Select Table'
        }
        isDisabled={
          event.status == EventDetailStatus.CLOSED || event.status !== EventDetailStatus.OPENED
        }
        isSeatSelected={false}
        isLoading={isLoading}
        onClick={onBottomClick}
        totalReservation={1}
        price={1000}
      />
    </React.Fragment>
  );
};

export default AdminTableReservationView;
